/**
 * The vocabulary of `signup_trigger` — every value that can be written, and the
 * rule deciding which of them may overwrite another.
 *
 * ## What signup_trigger is for
 *
 * One question: **why did this person create an account?** It is stored when a
 * signup flow starts, forwarded to the backend on the next request via
 * `x-mp-signup-trigger` (see events/mixpanelContext.ts), and stamped onto the
 * server-fired `user_signed_up` event. Alongside it travel three more
 * dimensions, and reading them together is the point:
 *
 *     signup_trigger    = "import_club"        <- the intent
 *     signup_section    = "Squad Builder"      <- the surface it started on
 *     signup_subsection = "see-active-squad"   <- the exact feature reached for
 *     signup_source_url = "/squad-builder"     <- normalised path, set automatically
 *
 * That composite is what answers "which flows actually convert" — the trigger
 * alone never could, since the same intent fires from half a dozen surfaces.
 *
 * ## Why overwriting is the hard part
 *
 * A signup is rarely one click. The club-import flow is the worst case:
 *
 *     /squad-builder  "Import Squad"        -> club-import gate  (the real intent)
 *       -> /club-import-tutorial            -> "Log in"          (a continuation)
 *         -> login modal                    -> account created
 *
 * Every one of those steps used to call storeSignupContext, last write winning,
 * so the recorded answer was always the last hop — `login-required` from
 * `/club-import-tutorial`. Both the intent and the origin surface were gone by
 * the time the account existed, and every club-import signup in Mixpanel looked
 * identical no matter where it started.
 *
 * Hence the split below. A **specific** trigger names an intent and may always
 * overwrite. A **continuation** trigger is a step inside a flow already under
 * way — it is stored only when nothing better is there, and never clobbers a
 * specific one. See shouldOverwriteSignupContext.
 */

/* -------------------------------------------------------------------------- */
/* Specific triggers — name an intent, always allowed to overwrite             */
/* -------------------------------------------------------------------------- */

/**
 * Written directly by the surface the user acted on, via storeSignupContext.
 *
 * Values are the literal strings already flowing into Mixpanel. They are not
 * stylistically consistent (see the KNOWN ISSUE at the bottom) and are kept
 * verbatim on purpose: renaming one silently splits its historical reports.
 */
export const SIGNUP_TRIGGERS = {
  /**
   * The user chose to start a club import. Pair it with a section naming where
   * they chose it — that pairing is the whole reason this trigger exists.
   *
   * Distinct from IMPORT_CLUB_LOGIN_WALL below, which is the mid-flow login
   * gate rather than the decision to import. Confusingly similar strings; the
   * two mean genuinely different things and both are worth keeping apart.
   */
  IMPORT_CLUB: "import_club",
  /** A generic call-to-action button. Only meaningful with its section. */
  CTA: "cta",
  SAVE_SQUAD: "save_squad",
  UNLOCK_CHAINS: "unlock_chains",
  CHANGE_EVO_STATE: "change_evo_state",
  FAVOURITE: "favourite",
  CHOOSE_CLUB_COUNTRY: "choose_club_country",
  SIGN_IN_TO_SUBSCRIBE: "sign-in-to-subscribe",
} as const;

/**
 * Written indirectly: `showModal({ modalType: "login-required",
 * freeFeatureLoggedInRequiredHook: X })` stores X as the trigger (see
 * redux/message/messageSlice.ts). Same field, different spelling convention,
 * because these grew up as modal props rather than as analytics values.
 */
export const LOGIN_GATE_TRIGGERS = {
  COMPLETE_SBC: "complete-sbc",
  FAVORITE_SBC: "favorite-sbc",
  FAVORITE_TACTIC: "favorite-tactic",
  HIDE_SBC: "hide-sbc",
  UPVOTE_DOWNVOTE_SBC: "upvote/downvote-sbc",
  PLAYER_EXCLUSION: "player-exclusion",
  PLAYER_INCLUSION: "player-inclusion",
  RESET_WEB_APP_STATE: "reset-web-app-state",
  SAVE_EVO: "save-evo",
  UNLOCK_CHAINS: "unlock-chains",
  CHANGE_EVO_STATE: "change-evo-state",
} as const;

/* -------------------------------------------------------------------------- */
/* Continuation triggers — steps inside a flow, never clobber a specific one   */
/* -------------------------------------------------------------------------- */

/**
 * The bare login wall: "you need an account to carry on". It says nothing about
 * why the user is here, so it is the fallback the messageSlice reducer reaches
 * for when a `login-required` modal is raised with no hook — which is most of
 * them. Perfectly good as the *only* answer; useless if it overwrote a real one.
 */
export const LOGIN_REQUIRED = "login-required";

/**
 * The login gate inside the import itself (useInitImport): the user already
 * decided to import, got as far as launching it, and only then hit the wall.
 *
 * A continuation, not an origin — if a gate or CTA already recorded where the
 * import was chosen, that answer is strictly better than this one. Kept as a
 * distinct value rather than folded into LOGIN_REQUIRED because when it *is*
 * the only answer (someone deep-linked straight to the mobile guide), knowing
 * they stalled mid-import is worth more than knowing they saw a login box.
 */
export const IMPORT_CLUB_LOGIN_WALL = "import-club";

/**
 * The full set. Everything not listed here is treated as specific — safer by
 * default, since a new trigger someone forgets to classify still records its
 * own intent instead of silently deferring to a stale one.
 */
export const CONTINUATION_TRIGGERS: readonly string[] = [
  LOGIN_REQUIRED,
  IMPORT_CLUB_LOGIN_WALL,
];

export const isContinuationTrigger = (trigger: string): boolean =>
  CONTINUATION_TRIGGERS.includes(trigger);

/* -------------------------------------------------------------------------- */
/* The precedence rule                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Whether an incoming trigger may replace what is already stored.
 *
 * Pure, and the single place the rule lives — storeSignupContext does the
 * storage, this decides. Three cases:
 *
 *  - nothing stored (or it expired, which the caller treats as nothing) -> yes
 *  - incoming is specific -> yes. A fresh deliberate intent beats a stale one;
 *    the alternative, first-write-wins, would let an abandoned gate from 59
 *    minutes ago outrank what the user is doing right now.
 *  - incoming is a continuation -> only over another continuation. This is the
 *    case that keeps "Import Squad in Squad Builder" alive across the hop to
 *    the tutorial's login modal.
 */
export const shouldOverwriteSignupContext = (
  incoming: string,
  stored: string | null,
): boolean => {
  if (!stored) return true;
  if (!isContinuationTrigger(incoming)) return true;
  return isContinuationTrigger(stored);
};

/* -------------------------------------------------------------------------- */
/* The override pattern — why some intents appear under two spellings          */
/* -------------------------------------------------------------------------- */
/*
  Two intents exist as both a snake_case and a kebab-case value:

      unlock_chains    vs  unlock-chains
      change_evo_state vs  change-evo-state

  Deliberate, not a bug. A handler dispatches the login modal — whose reducer
  writes the kebab value from freeFeatureLoggedInRequiredHook — and then
  immediately calls storeSignupContext with the snake value, overwriting it in
  the same tick. Both are specific triggers, so the second write wins and the
  kebab value never survives to be read. The snake value is the tracking-plan
  name; the kebab one is a modal prop that doubles as a fallback. See
  handleClickUnlockChains in EvolutionsBuilder.tsx, and the comment at
  DetailedEvoPage.tsx that names the override explicitly. The reporting numbers
  bear it out — `unlock_chains` carries the traffic, `unlock-chains` sees none.

  Fragile, though: the two writes live in different layers (a reducer and a
  component) with nothing enforcing the order. Reorder a handler and the value
  silently flips. If you touch one of these handlers, check the pairing still
  holds.

  `save_evo_player` used to be a third pair, and was the one case where the
  override did NOT hold everywhere: SaveEvolutions renders from three places and
  only EvolutionsBuilder wrapped setAwaitingLogin to apply it, so the same
  intent reported differently depending on the page. Collapsed onto `save-evo`
  (2026-08-12) — the value the other two pages were already sending. Nothing
  writes `save_evo_player` now; historical events keep it.
*/
